import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import ConfirmationDialog from '../../../components/ConfirmationDialog';
import InputForm from '../../../components/inputForm/InputForm';
import InputFormSkeleton from '../../../components/inputForm/InputFormSkeleton';

import { callUIAPI } from '../../../utils/apiUtils';
import * as apiConstants from '../../../utils/apiUtilsConstants';
import { showErrorMessage, showSuccessMessage } from '@/shared/components/toast-system';
import { getUserReadableError } from '../../../utils/utils';
import * as compConstants from '../../../components/componentConstants';
import { ENTITY_NAME_USER } from '../../../dao/dbConstants';
import { ROUTE_FIRM_EDIT } from '../../../constants';

const layoutType = compConstants.INPUT_LAYOUT_TYPE_SEPARATE_LABEL;

const FirmUser = () => {
  const { firmId, userId: userIdStr } = useParams();
  const userId = userIdStr ? parseInt(userIdStr) : 0;
  const navigate = useNavigate();

  const [record, setRecord] = useState(null);
  const [originalRecord, setOriginalRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const backRoute = ROUTE_FIRM_EDIT.replace(':id?', firmId) + '?tab=users';

  const navigateBack = () => {
    navigate(backRoute);
  };

  const USER_CONFIG = {
    sections: [
      {
        heading: '',
        inputs: [
          {
            fieldName: 'firstName', label: 'First Name', tooltipText: '', layoutType,
            validationRules: { isRequired: true, maxLength: 100 },
          },
          {
            fieldName: 'middleName', label: 'Middle Name', tooltipText: '', layoutType,
            validationRules: { isRequired: false, maxLength: 10 },
          },
          {
            fieldName: 'lastName', label: 'Last Name', tooltipText: '', layoutType,
            validationRules: { isRequired: false, maxLength: 100 },
          },
          {
            fieldName: 'email', label: 'Email', tooltipText: '', layoutType,
            validationRules: { isRequired: true, maxLength: 100, isEmail: true },
            isDisabled: (rec) => !!rec.primaryUserId && rec.id === rec.primaryUserId,
          },
          {
            fieldName: 'homePhone', label: 'Home Phone', tooltipText: '', layoutType,
            validationRules: { isRequired: false, maxLength: 25 },
          },
          {
            fieldName: 'cellPhone', label: 'Cell Phone', tooltipText: '', layoutType,
            validationRules: { isRequired: false, maxLength: 25 },
          },
          {
            fieldName: 'externalId', label: 'External Id', tooltipText: '', layoutType,
            validationRules: { maxLength: 250 },
          },
          {
            fieldName: 'isActive', label: 'Active?',
            inputType: compConstants.INPUT_TYPE_CHECKBOX,
            layoutType: compConstants.INPUT_LAYOUT_TYPE_INLINE,
            isHidden: !userId,
          },
        ],
      },
    ],
  };

  useEffect(() => {
    if (!userId || !firmId) return;
    setLoading(true);
    callUIAPI(`bo/firms/${firmId}/users/${userId}`, null, apiConstants.METHOD_GET, true)
      .then((response) => {
        const user = response.data.item;
        setRecord(user);
        setOriginalRecord(user);
      })
      .catch((err) => {
        showErrorMessage(err?.response?.data?.message || 'Failed to load user.');
      })
      .finally(() => setLoading(false));
  }, [userId, firmId]);

  const handleSave = async (values) => {
    const isEmailChanged = !!originalRecord?.email && values?.email !== originalRecord?.email;
    setSaving(true);
    try {
      const response = await callUIAPI(
        `bo/firms/${firmId}/users/${userId}`,
        values,
        apiConstants.METHOD_PUT,
        true,
      );
      const savedUser = response.data.item;
      setOriginalRecord(savedUser);
      setRecord(savedUser);

      let successMessage = 'User updated successfully.';
      if (isEmailChanged) {
        successMessage = 'User updated successfully. An email has been sent to the new address for account security.';
      }
      showSuccessMessage(successMessage);
    } catch (err) {
      showErrorMessage(getUserReadableError(err) ?? 'Failed to save user.');
    }
    setSaving(false);
  };

  const handleSubmit = async (entityName, values) => {
    if (originalRecord && values?.email && values.email !== originalRecord?.email) {
      setRecord(values);
      setShowConfirm(true);
      return;
    }
    await handleSave(values);
    navigateBack();
  };

  const handleConfirm = () => {
    setShowConfirm(false);
    if (record) {
      handleSave(record).then(() => {
        navigateBack();
      });
    }
  };

  const handleCancel = () => {
    setShowConfirm(false);
  };

  /**
   * The form is mounted only once the record is here, because InputForm fetches
   * the record itself whenever it is given an id without one. It would fetch
   * from the path the entity name maps to, and this user is only readable
   * through the firm — `/users/:userId` is a firm route scoped to the caller's
   * own firm, which a back-office operator does not have.
   */
  if (loading) return <InputFormSkeleton />;
  if (!record) return null;

  return (
    <>
      <InputForm
        entityName={ENTITY_NAME_USER}
        inputConfig={USER_CONFIG}
        heading=""
        id={userId}
        initialRecord={record}
        initialValues={record}
        saving={saving}
        beforeSave={handleSubmit}
        doNotSave={true}
        backRoute={backRoute}
      />

      {showConfirm && (
        <ConfirmationDialog
          isOpen={showConfirm}
          title="Confirm Email Change"
          message="Are you sure you want to change the user's email address? The user will be logged out and must reset their password to secure their account."
          icon="warning"
          onConfirm={handleConfirm}
          onCancel={handleCancel}
          confirmLabel="Yes, Update"
          cancelLabel="No, Cancel"
        />
      )}
    </>
  );
};

export default FirmUser;